package org.example.project.engineslot

import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlin.js.Date
import org.example.project.manager.LumaticaApiManager
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.service.LoginService
import org.example.project.service.YardApi
import org.example.project.service.YardEngineApi
import org.example.project.service.ZustandManager
import org.example.project.utils.All

private val slotJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

suspend fun loadSlotConfig(providerFirmenId: String = "", standortId: String = ""): All {
    val normalizedFirmenId = providerFirmenId.trim().ifBlank { currentSlotProviderFirmenId() }
    val normalizedStandortId = standortId.trim()
    val remote = if (normalizedFirmenId.isBlank()) {
        null
    } else {
        runCatching {
            LumaticaApiManager.call("slot/config/${apiPart(normalizedFirmenId)}/${apiPart(normalizedStandortId)}")
                .takeIf { it.isNotBlank() && it != "null" }
                ?.let { slotJson.decodeFromString(All.AllNames.serializer(), it) }
        }.getOrNull()
    }
    return remote ?: slotDefaultConfig()
}

suspend fun saveSlotConfig(config: All, providerFirmenId: String = "", standortId: String = "") {
    val normalizedFirmenId = providerFirmenId.trim().ifBlank { currentSlotProviderFirmenId() }
    val normalizedStandortId = standortId.trim()
    if (normalizedFirmenId.isNotBlank()) {
        runCatching {
            LumaticaApiManager.call(
                "slot/config-save/${apiPart(normalizedFirmenId)}/${apiPart(normalizedStandortId)}/${apiPart(slotConfigSaveBody(config))}"
            )
        }
    }
}

private fun apiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private val slotConfigExtraKeys = listOf(
    SLOT_EXTRA_INTERVAL,
    SLOT_EXTRA_COUNT,
    SLOT_EXTRA_COUNT_WE,
    SLOT_EXTRA_COUNT_WA,
    SLOT_EXTRA_DISABLED_CELLS,
    SLOT_EXTRA_BOOKING_LEAD_HOURS,
    SLOT_EXTRA_BOOKING_LEAD_DAYS
)

private val slotBookingExtraKeys = listOf(
    SLOT_EXTRA_INDEX,
    SLOT_EXTRA_BEREICH,
    SLOT_EXTRA_DISABLED_CELLS,
    SLOT_EXTRA_ABHOLORT,
    SLOT_EXTRA_BOOKING_DATE,
    SLOT_EXTRA_STANDORT_ID,
    SLOT_EXTRA_LADEN_TOUREN,
    SLOT_EXTRA_LIEFERN_BELEGE,
    SLOT_EXTRA_DRIVER_ID,
    "slotYardLkwId",
    "slotYardStandortKey",
    SLOT_EXTRA_LIEFERANT_FIRMEN_ID,
    SLOT_EXTRA_UNTERNEHMER_FIRMEN_ID,
    "slotEmpfaengerFirmenId",
    "slotAuftraggeberFirmenId",
    "anbieterFirmenId",
    "anbieterUserId",
    "anbieterVorname",
    "anbieterNachname",
    "anbieterName"
)

private fun slotActivityReferenceLabel(entry: All): String =
    entry.lieferscheinnummer.ifBlank { "FEHLER_LS_FEHLT" }

private fun slotActivityPalletLabel(entry: All): String =
    entry.palettenanzahl.takeIf { it > 0 }?.let { " ${it} Paletten" }.orEmpty()

private fun slotFeedTime(timestamp: Long): String {
    val date = Date(timestamp.toDouble())
    return "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
}

private fun vorgangslogEntry(
    eventType: String,
    message: String,
    after: All,
    before: All? = null
): All {
    val timestamp = Date().getTime().toLong()
    return All(
        typ = eventType,
        notiz = message,
        feedArt = eventType,
        feedEvent = message,
        feedEventLabel = message,
        feedLsNummer = after.lieferscheinnummer,
        feedUhrzeit = slotFeedTime(timestamp),
        firmenId = after.firmenId.ifBlank { LoginService.getUser()?.firmenId.orEmpty() },
        firma = after.firma,
        lfdNr = after.lfdNr,
        lieferscheinnummer = after.lieferscheinnummer,
        lkwkennzeichen = after.lkwkennzeichen,
        auftragsart = after.auftragsart,
        palettenanzahl = after.palettenanzahl,
        fahrername = after.fahrername,
        zustand = after.zustand,
        datum = after.datum,
        uhrzeitAnmeldung = after.uhrzeitAnmeldung,
        timestamp = timestamp.toString(),
        status = after.zustand.toString()
    )
}

private fun slotConfigSaveBody(config: All): String =
    buildJsonObject {
        put("id", JsonPrimitive(config.id))
        put("bezeichnung", JsonPrimitive(config.bezeichnung))
        put("standort", JsonPrimitive(config.standort))
        put("uhrzeitVon", JsonPrimitive(config.uhrzeitVon))
        put("uhrzeitBis", JsonPrimitive(config.uhrzeitBis))
        put("slotIntervallMinuten", JsonPrimitive(config.slotIntervallMinuten))
        put("slotAnzahl", JsonPrimitive(config.slotAnzahl))
        put("slotAnzahlWe", JsonPrimitive(config.slotAnzahlWe))
        put("slotAnzahlWa", JsonPrimitive(config.slotAnzahlWa))
        put("slotDeaktivierteFenster", JsonPrimitive(config.slotDeaktivierteFenster))
        put("slotBuchungVorlaufStunden", JsonPrimitive(config.slotBuchungVorlaufStunden))
        put("slotBuchungVorlaufTage", JsonPrimitive(config.slotBuchungVorlaufTage))
    }.toString()

private fun decodeBookingList(raw: String): List<All> =
    if (raw.isBlank() || raw == "null") emptyList()
    else runCatching { slotJson.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())

private const val SLOT_PRIO_START = 11

private suspend fun currentSlotProviderFirmenId(): String =
    LoginService.getUser()?.firmenId
        .orEmpty()
        .trim()

private fun currentSlotProviderUserId(): String =
    LoginService.getUser()?.id.orEmpty().trim()

private fun currentSlotProviderUserName(): Triple<String, String, String> {
    val user = LoginService.getUser()
    val vorname = user?.vorname.orEmpty().trim()
    val nachname = user?.nachname.orEmpty().trim()
    val name = listOf(vorname, nachname).filter { it.isNotBlank() }.joinToString(" ")
        .ifBlank { user?.name.orEmpty().trim() }
        .ifBlank { user?.userName.orEmpty().trim() }
    return Triple(vorname, nachname, name)
}

private fun slotYardFirmenIdForBooking(booking: All): String =
    booking.firmenId.trim()

private suspend fun slotApiBooking(booking: All): All {
    val yardFirmenId = slotYardFirmenIdForBooking(booking).ifBlank { error("Slot-Firma fehlt") }
    val providerFirmenId = currentSlotProviderFirmenId().ifBlank { error("Anbieter-Firma fehlt") }
    val providerUserId = currentSlotProviderUserId().ifBlank { error("Anbieter-Benutzer fehlt") }
    val (providerVorname, providerNachname, providerName) = currentSlotProviderUserName()
    return booking.copy(
        firmenId = yardFirmenId,
        vorname = booking.vorname.ifBlank { providerVorname },
        nachname = booking.nachname.ifBlank { providerNachname },
        name = booking.name.ifBlank { providerName },
        datum = booking.datum,
        slotDatum = booking.slotDatum.ifBlank { booking.datum },
        slotBereich = booking.slotBereich.ifBlank { booking.auftragsart },
        slotStandortId = booking.slotStandortId.ifBlank { booking.standort },
        anbieterFirmenId = providerFirmenId,
        anbieterUserId = providerUserId,
        anbieterVorname = booking.vorname.ifBlank { providerVorname },
        anbieterNachname = booking.nachname.ifBlank { providerNachname },
        anbieterName = booking.name.ifBlank { providerName }
    )
}

private suspend fun slotApiBookings(entries: List<All>): List<All> =
    entries.map { slotApiBooking(it) }

suspend fun loadSlotBookings(providerFirmenId: String = ""): List<All> =
    runCatching { providerFirmenId.trim().ifBlank { currentSlotProviderFirmenId() } }.getOrDefault("")
        .takeIf { it.isNotBlank() }
        ?.let { normalizedProviderFirmenId ->
            runCatching {
                LumaticaApiManager.call("slot/bookings/${apiPart(normalizedProviderFirmenId)}")
            }.getOrDefault("")
                .let(::decodeBookingList)
        }
        ?: emptyList()

suspend fun searchCompanyDriverSlotBookings(
    providerFirmenId: String,
    driverIds: List<String>,
    filters: Map<String, String>
): List<All> =
    loadWithTabularFilters(
        "slot/company-driver-bookings/${apiPart(providerFirmenId)}",
        filters,
        mapOf("driverIds" to driverIds.filter { it.isNotBlank() }.distinct().joinToString(","))
    ).let(::decodeBookingList)

suspend fun searchSlotDriverSummary(filters: Map<String, String>): List<All> =
    loadWithTabularFilters("slot/driver-summary", filters).let(::decodeBookingList)

suspend fun saveSlotBooking(booking: All): List<All> {
    val apiBooking = slotApiBooking(booking)
    val providerFirmenId = apiBooking.firmenId
    if (providerFirmenId.isBlank()) error("Slot-Firma fehlt")
    if (slotHasBookingData(apiBooking) && apiBooking.zustand != ZustandManager.Slot.FREI) {
        validateSlotBookingReferenceAvailable(apiBooking)
    }
    val existingBookings = runCatching { loadSlotBookings(providerFirmenId) }.getOrDefault(emptyList())
    val bookingWithPrio = slotBookingWithDerivedYardPrio(apiBooking, existingBookings)
    syncSlotBookingToYard(bookingWithPrio)
    return loadSlotBookings(providerFirmenId)
}

suspend fun syncSlotBookings(entries: List<All>): List<All> {
    if (entries.isEmpty()) return emptyList()
    val apiBookings = slotApiBookings(entries)
    val providerFirmenId = apiBookings.first().firmenId
    if (providerFirmenId.isBlank()) error("Slot-Firma fehlt")
    syncBookingsToYard(apiBookings.filter { slotHasBookingData(it) })
    return loadSlotBookings(providerFirmenId)
}

suspend fun deleteSlotBooking(booking: All) {
    val apiBooking = slotApiBooking(booking)
    when (slotBookingArtCode(apiBooking)) {
        SLOT_BEREICH_WE -> cancelLinkedWeBooking(apiBooking)
        SLOT_BEREICH_WA -> cancelLinkedWaBooking(apiBooking)
        else -> error("Auftragsart fehlt")
    }
}

private suspend fun syncBookingsToYard(bookings: List<All>) {
    bookings
        .forEach { syncSlotBookingToYard(it) }
}

private fun yardFirmenId(booking: All): String =
    booking.firmenId.trim().ifBlank { error("Slot-Firma fehlt") }

private suspend fun slotBookingWithDerivedYardPrio(booking: All, existingBookings: List<All>): All {
    if (booking.prio.isNotBlank() && booking.ablaufPrio > 0) return booking
    val standortId = booking.slotStandortId.ifBlank { booking.standort }
    val config = runCatching { loadSlotConfig(booking.firmenId, standortId) }.getOrDefault(slotDefaultConfig())
    val windows = slotTimeWindows(config).map { it.uhrzeitVon }
    val capacity = slotCount(config)
    val group = (existingBookings.filter {
            it.datum == booking.datum &&
            slotBookingArtCode(it) == slotBookingArtCode(booking) &&
            it.slotStandortId.ifBlank { it.standort } == standortId
    } + booking).distinctBy { slotBookingOrderKey(it) }
    val fallbackTimes = group.map { it.uhrzeitVon }.filter { it.isNotBlank() }.distinct()
        .sortedBy { parseTimeMinutes(it) ?: Int.MAX_VALUE }
    val blockIndex = windows.indexOf(booking.uhrzeitVon).takeIf { it >= 0 }
        ?: fallbackTimes.indexOf(booking.uhrzeitVon).takeIf { it >= 0 }
        ?: 0
    val sameWindow = group.filter { it.uhrzeitVon == booking.uhrzeitVon }
        .sortedWith(compareBy<All> { parseTimeMinutes(it.uhrzeitVon) ?: Int.MAX_VALUE }
            .thenBy { slotBookingOrderKey(it) })
    val position = sameWindow.indexOfFirst { slotBookingOrderKey(it) == slotBookingOrderKey(booking) }.coerceAtLeast(0)
    val prio = SLOT_PRIO_START + (blockIndex * capacity) + position
    return booking.copy(
        prio = booking.prio.ifBlank { prio.toString() },
        ablaufPrio = booking.ablaufPrio.takeIf { it > 0 } ?: prio
    )
}

private fun slotBookingOrderKey(entry: All): String =
    entry.id.ifBlank {
        listOf(
            entry.lieferscheinnummer,
            entry.datum,
            entry.uhrzeitVon,
            entry.slotIndex,
            entry.slotStandortId.ifBlank { entry.standort }
        ).joinToString("|")
    }

private fun slotSameYardReference(left: All, right: All): Boolean =
    when (slotBookingArtCode(right)) {
        SLOT_BEREICH_WE -> weSameReference(left, weReferenceKey(right))
        SLOT_BEREICH_WA -> waSameReference(left, waReferenceKey(right, slotLadenTouren(right).firstOrNull() ?: right))
        else -> false
    }

private suspend fun validateSlotBookingReferenceAvailable(booking: All) {
    when (slotBookingArtCode(booking)) {
        SLOT_BEREICH_WE -> {
            val references = slotLiefernBelege(booking)
                .map { it.lieferscheinnummer.ifBlank { booking.lieferscheinnummer }.trim() }
                .filter { it.isNotBlank() }
            if (references.isEmpty()) error("WE LS fehlt")
            val duplicate = references
                .groupingBy { it }
                .eachCount()
                .filterValues { it > 1 }
                .keys
                .firstOrNull()
            if (duplicate != null) error("WE LS $duplicate doppelt im Auftrag")
        }

        SLOT_BEREICH_WA -> {
            val duplicate = slotLadenTouren(booking)
                .map { waReferenceKey(booking, it) }
                .filter { it.isNotBlank() }
                .groupingBy { it }
                .eachCount()
                .filterValues { it > 1 }
                .keys
                .firstOrNull()
            if (duplicate != null) error("WA LS $duplicate doppelt im Auftrag")
        }

        else -> error("Auftragsart fehlt")
    }
}

private suspend fun syncSlotBookingToYard(booking: All) {
    if (!YardEngineApi.syncSlotBooking(booking, yardFirmenId(booking), slotYardSyncAction(booking))) {
        error("Slot-Auftrag konnte nicht im Yard angelegt werden.")
    }
}

private fun slotYardSyncAction(booking: All): String =
    when (slotBookingArtCode(booking)) {
        SLOT_BEREICH_WE -> "we-${slotBookingFlowMode(booking)}"
        SLOT_BEREICH_WA -> "wa-${slotBookingFlowMode(booking)}"
        else -> "sync"
    }

private fun waReferenceKey(booking: All, tour: All): String =
    tour.lieferscheinnummer.ifBlank { tour.lieferscheinnummer }.ifBlank { booking.lieferscheinnummer }.ifBlank { booking.lieferscheinnummer }.trim()

private fun weReferenceKey(booking: All): String {
    val beleg = slotLiefernBelege(booking).firstOrNull() ?: booking
    return beleg.lieferscheinnummer.ifBlank { booking.lieferscheinnummer }.trim()
}

private fun weSameReference(entry: All, reference: String): Boolean {
    val normalized = reference.trim()
    return normalized.isNotBlank() && entry.lieferscheinnummer.trim() == normalized
}

private fun waSameReference(entry: All, reference: String): Boolean {
    val normalized = reference.trim()
    return normalized.isNotBlank() && (
        entry.lieferscheinnummer.trim() == normalized ||
            entry.lieferscheinnummer.trim() == normalized
        )
}

private suspend fun cancelLinkedWeBooking(booking: All) {
    if (!YardEngineApi.cancelSlotBooking(booking, yardFirmenId(booking), "we-${slotBookingFlowMode(booking)}-cancel")) {
        error("WE-Slot kann nicht gelöscht werden.")
    }
}

private suspend fun cancelLinkedWaBooking(booking: All) {
    if (!YardEngineApi.cancelSlotBooking(booking, yardFirmenId(booking), "wa-${slotBookingFlowMode(booking)}-cancel")) {
        error("WA-Slot kann nicht gelöscht werden.")
    }
}
